import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type PillVariant = 'success' | 'warning' | 'danger' | 'info' | 'primary' | 'neutral' | 'brand';

interface PillProps {
	variant: PillVariant;
	size?: 'sm' | 'md';
	icon?: LucideIcon;
	children: ReactNode;
}

const variantClasses: Record<PillVariant, string> = {
	success: 'bg-success text-success-fg',
	warning: 'bg-warning text-warning-fg',
	danger: 'bg-danger text-danger-fg',
	info: 'bg-info text-info-fg',
	primary: 'bg-brand-subtle text-brand-subtle-fg',
	neutral: 'bg-neutral text-neutral-fg',
	// Solid brand "MAIN/DEFAULT" badge: carries the badge's own padding/weight/casing so it
	// renders identically to the former hand-rolled spans (twMerge resolves the base conflicts).
	brand: 'bg-brand px-1.5 font-semibold uppercase tracking-[0.04em] text-on-brand'
};

/** Pure presentational status badge. Tokens flip with the active theme. */
export function Pill({ variant, size = 'sm', icon: Icon, children }: PillProps) {
	const textSize = size === 'sm' ? 'text-[10px]' : 'text-[11px]';
	const iconPx = size === 'sm' ? 10 : 11;
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 whitespace-nowrap rounded-pill px-2 py-0.5 font-medium leading-none',
				textSize,
				variantClasses[variant]
			)}>
			{Icon && <Icon size={iconPx} />}
			{children}
		</span>
	);
}
